import { useQuery } from '@tanstack/react-query'
import { OpalApiRequestParams, OpalApiResponseDocument } from '@opallabs/api'
import { boards } from '@opallabs/api/v3'
import { globalQueryKeys } from '../../queryKeys'

type ReadBoardsResponse = OpalApiResponseDocument<'ReadBoardsV3', 200>

type ReadBoardsQuery = OpalApiRequestParams<'ReadBoardsV3'>['query']

type UseReadBoardsParams = {
  query: ReadBoardsQuery
  workspaceUuid: string
}

type UseReadBoardsOptions<T = ReadBoardsResponse> = UseReadBoardsParams & {
  /** Set false to hold the fetch back until the list is wanted. */
  enabled?: boolean
  select?: (data: ReadBoardsResponse) => T
}

/** How many boards one request carries. A workspace can hold hundreds. */
const PAGE_LIMIT = 300

const readPage = (query: ReadBoardsQuery, offset: number) =>
  boards.readCollection({
    params: { query: { ...query, page: { limit: PAGE_LIMIT, offset } } },
  })

/**
 * Every board the query names, however many pages it takes.
 *
 * A single request would answer with one page and say nothing about the rest,
 * so a workspace with more boards than a page holds would quietly lose the
 * ones past the end — and a filter offering only some of the boards hides work
 * rather than finding it. The pages are read one after another, which is
 * enough: a workspace holds hundreds of boards, not thousands.
 */
const readAllBoards = async (
  query: ReadBoardsQuery
): Promise<ReadBoardsResponse> => {
  const first = await readPage(query, 0)
  const rest: ReadBoardsResponse[] = []

  for (
    let offset = PAGE_LIMIT;
    offset < first.meta.total;
    offset += PAGE_LIMIT
  ) {
    rest.push(await readPage(query, offset))
  }

  return rest.length === 0
    ? first
    : {
        ...first,
        data: [first, ...rest].flatMap((page) => page.data),
        included: [first, ...rest].flatMap((page) => page.included ?? []),
      }
}

export const useReadBoards = <T = ReadBoardsResponse>({
  enabled,
  query,
  select,
  workspaceUuid,
}: UseReadBoardsOptions<T>) => {
  const queryParam: ReadBoardsQuery = {
    ...query,
    filter: {
      ...query.filter,
      workspace_id: [workspaceUuid],
    },
  }
  return useQuery({
    enabled,
    queryKey: globalQueryKeys.boardsByWorkspace(workspaceUuid, queryParam),
    queryFn: () =>
      // A caller that asked for one page of its own gets the page it asked
      // for; only the whole list is drained.
      queryParam.page === undefined
        ? readAllBoards(queryParam)
        : boards.readCollection({ params: { query: queryParam } }),
    select,
  })
}
